import { STATS_INTERVAL, type StatsInterval } from '@/lib/leads';
import { DAY_MS, STATS_TIME_ZONE, periodLabel, rangeWindow } from '@/lib/report-window';

/**
 * The Analytics → PPC page's window: which days it covers and how it buckets
 * them. Pure functions over the URL params, so a view can be bookmarked or
 * shared and every section reads the same window.
 *
 * URL shape (all optional; the default is the last 12 months by month):
 * - `?range=7d|30d|90d|12m|24m|36m|all` a preset ending today.
 * - `?range=custom|day|week|month&from=YYYY-MM-DD&to=YYYY-MM-DD` a picked
 *   window, both days inclusive. `day`, `week` and `month` only change which
 *   picker edits it.
 * - `?interval=day|week|month` the bucket size; left out, the window's own
 *   default applies, and one that makes no sense for the window falls back to
 *   the nearest that does.
 */

/** The account's first month of spend; "All time" starts here. */
export const ACCOUNT_START = '2015-11-01';

/** Longest window the API buckets by day (PPC_DAILY_MAX_DAYS in apps/api ads.schemas.ts). */
export const PPC_DAILY_MAX_DAYS = 400;

export const PPC_PRESETS = {
  '7d': { label: '7 days', shortLabel: '7d' },
  '30d': { label: '30 days', shortLabel: '30d' },
  '90d': { label: '90 days', shortLabel: '90d' },
  '12m': { label: '12 months', shortLabel: '12m' },
  '24m': { label: '24 months', shortLabel: '24m' },
  '36m': { label: '36 months', shortLabel: '36m' },
  all: { label: 'All time', shortLabel: 'All' },
} as const satisfies Record<string, { label: string; shortLabel: string }>;
export type PpcPreset = keyof typeof PPC_PRESETS;
export const PPC_PRESET_KEYS = Object.keys(PPC_PRESETS) as PpcPreset[];
export const DEFAULT_PRESET: PpcPreset = '12m';

/** The pickers behind "Pick dates": a from/to range, or one day, week or month. */
export const PICK_MODE = { custom: 'custom', day: 'day', week: 'week', month: 'month' } as const;
export type PickMode = (typeof PICK_MODE)[keyof typeof PICK_MODE];
export const PICK_MODE_LABELS: Readonly<Record<PickMode, string>> = {
  custom: 'Date range',
  day: 'One day',
  week: 'One week',
  month: 'One month',
};
export const PICK_MODES = Object.keys(PICK_MODE_LABELS) as PickMode[];

export const PPC_WINDOW_PARAM = { range: 'range', from: 'from', to: 'to', interval: 'interval' } as const;

export type PpcSelection =
  | { readonly kind: 'preset'; readonly preset: PpcPreset }
  | { readonly kind: 'pick'; readonly mode: PickMode; readonly from: string; readonly to: string };

/** The window the API reads: [from, to) with `to` exclusive, plus how to bucket it. */
export interface PpcWindow {
  readonly from: string;
  readonly to: string;
  /** Days in the window, both ends counted. */
  readonly days: number;
  readonly interval: StatsInterval;
  /** Intervals that make sense for this window, finest first. */
  readonly intervals: ReadonlySet<StatsInterval>;
  /** Whether the tiles compare against the same-length window before. */
  readonly compare: boolean;
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const INTERVAL_ORDER: readonly StatsInterval[] = [STATS_INTERVAL.day, STATS_INTERVAL.week, STATS_INTERVAL.month];

/** Fewest days for weekly buckets (two whole weeks) and for monthly ones (about two months). */
const WEEKLY_MIN_DAYS = 14;
const MONTHLY_MIN_DAYS = 60;
/** Longest window that opens daily, and longest that opens weekly. */
const DAILY_DEFAULT_MAX_DAYS = 31;
const WEEKLY_DEFAULT_MAX_DAYS = 120;

// ─── Calendar math (calendar days only, no time zone) ──────────────────────

const dayNumber = (ymd: string): number => Math.floor(Date.parse(`${ymd}T00:00:00Z`) / DAY_MS);
const fromDayNumber = (n: number): string => new Date(n * DAY_MS).toISOString().slice(0, 10);
export const addDays = (ymd: string, days: number): string => fromDayNumber(dayNumber(ymd) + days);

function isDay(value: string | null): value is string {
  return value !== null && YMD.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && fromDayNumber(dayNumber(value)) === value;
}

/** Today on Eastern time, the zone the API buckets on. */
export function todayEastern(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: STATS_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** Monday of the week holding `ymd`. */
export function mondayOf(ymd: string): string {
  const n = dayNumber(ymd);
  return fromDayNumber(n - ((((n + 3) % 7) + 7) % 7)); // 1970-01-01 was a Thursday
}

/** First and last day of the month `yyyy-mm`. */
export function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return { from: `${month}-01`, to: new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10) };
}

/** The last `count` months, newest first, as `yyyy-mm`. */
export function recentMonths(today: string, count: number): string[] {
  const [y, m] = today.split('-').map(Number) as [number, number];
  return Array.from({ length: count }, (_, i) => new Date(Date.UTC(y, m - 1 - i, 1)).toISOString().slice(0, 7));
}

/** The picked window for each picker, around `anchor` (inclusive days). */
export function pickWindow(mode: PickMode, anchor: string, current?: { from: string; to: string }): { from: string; to: string } {
  switch (mode) {
    case PICK_MODE.day:
      return { from: anchor, to: anchor };
    case PICK_MODE.week: {
      const from = mondayOf(anchor);
      return { from, to: addDays(from, 6) };
    }
    case PICK_MODE.month:
      return monthBounds(anchor.slice(0, 7));
    case PICK_MODE.custom:
      return current ?? { from: anchor, to: anchor };
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
}

// ─── URL → selection → window ──────────────────────────────────────────────

/** Read the selection from the URL; anything malformed falls back to the default preset. */
export function parseSelection(params: URLSearchParams): PpcSelection {
  const range = params.get(PPC_WINDOW_PARAM.range);
  if (range !== null && range in PPC_PRESETS) return { kind: 'preset', preset: range as PpcPreset };
  const mode = PICK_MODES.find((m) => m === range);
  const from = params.get(PPC_WINDOW_PARAM.from);
  const to = params.get(PPC_WINDOW_PARAM.to);
  if (mode !== undefined && isDay(from)) {
    const end = isDay(to) && to >= from ? to : from;
    return { kind: 'pick', mode, from, to: end };
  }
  return { kind: 'preset', preset: DEFAULT_PRESET };
}

/** Which intervals a window of `days` days can be bucketed by. */
export function validIntervals(days: number): ReadonlySet<StatsInterval> {
  const valid = new Set<StatsInterval>();
  if (days <= PPC_DAILY_MAX_DAYS) valid.add(STATS_INTERVAL.day);
  if (days >= WEEKLY_MIN_DAYS) valid.add(STATS_INTERVAL.week);
  if (days >= MONTHLY_MIN_DAYS) valid.add(STATS_INTERVAL.month);
  return valid;
}

/** The interval a window opens on: daily up to a month, weekly up to a quarter, monthly beyond. */
export function defaultInterval(days: number): StatsInterval {
  if (days <= DAILY_DEFAULT_MAX_DAYS) return STATS_INTERVAL.day;
  if (days <= WEEKLY_DEFAULT_MAX_DAYS) return STATS_INTERVAL.week;
  return STATS_INTERVAL.month;
}

/** `wanted` when it fits the window, else the nearest interval that does (the coarser on a tie). */
export function resolveInterval(wanted: string | null, days: number): StatsInterval {
  const valid = validIntervals(days);
  const index = INTERVAL_ORDER.findIndex((i) => i === wanted);
  if (index === -1) return defaultInterval(days);
  const byDistance = INTERVAL_ORDER.map((interval, i) => ({ interval, distance: Math.abs(i - index) + (i < index ? 0.5 : 0) }))
    .filter(({ interval }) => valid.has(interval))
    .sort((a, b) => a.distance - b.distance);
  return byDistance[0]?.interval ?? defaultInterval(days);
}

/**
 * The window for a selection. Picked windows stop at today: a week or month
 * still under way compares against the same number of days before it, not a
 * whole one.
 */
export function ppcWindow(selection: PpcSelection, wantedInterval: string | null, now: Date = new Date()): PpcWindow {
  const tomorrow = addDays(todayEastern(now), 1);
  let from: string;
  let to: string;
  if (selection.kind === 'preset') {
    const preset = selection.preset;
    ({ from, to } = preset === 'all' ? { from: ACCOUNT_START, to: rangeWindow('12m', now).to } : rangeWindow(preset, now));
  } else {
    from = selection.from;
    to = addDays(selection.to, 1) < tomorrow ? addDays(selection.to, 1) : tomorrow;
    if (to <= from) to = addDays(from, 1);
  }
  const days = dayNumber(to) - dayNumber(from);
  return {
    from,
    to,
    days,
    interval: resolveInterval(wantedInterval, days),
    intervals: validIntervals(days),
    compare: !(selection.kind === 'preset' && selection.preset === 'all'),
  };
}

// ─── Labels ────────────────────────────────────────────────────────────────

/** A bucket's label: "Oct 7" for a day, "Week of Oct 6" for a week, "Oct 2026" for a month. */
export function ppcPeriodLabel(period: string, interval: StatsInterval): string {
  const label = periodLabel(period, interval);
  return interval === STATS_INTERVAL.week ? `Week of ${label}` : label;
}

const dayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const dayFormatNoYear = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const asDate = (ymd: string): Date => new Date(`${ymd}T00:00:00Z`);

/** "Oct 6, 2026" for one day; "Oct 6 to Oct 12, 2026" (or across years, both years) for more. */
export function windowLabel(window: { readonly from: string; readonly to: string }): string {
  const last = addDays(window.to, -1);
  if (last <= window.from) return dayFormat.format(asDate(window.from));
  const sameYear = window.from.slice(0, 4) === last.slice(0, 4);
  return `${(sameYear ? dayFormatNoYear : dayFormat).format(asDate(window.from))} to ${dayFormat.format(asDate(last))}`;
}
